import React, { useEffect, useRef, useState } from "react";
import Header from "./Header";
import {
  Box,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useLocation, useNavigate } from "react-router-dom";

import ChatBotContainer from "../../../components/common/chat/chatBotContainer";
import CommonSubsectionUI from "../components/commonSubsectionUI";
import formatLanguage from "../../../utils/formatLanguage";
import { useSelector } from "react-redux";
import SubsectionList from "./SubsectionList";
import apiClient from "../../../services/apiClient";

// Subsection Layout Imports
import ViewSubsectionSteps from "../components/viewSubsectionSteps";
import ViewSubsectionAccordion from "../components/viewSubsectionAccordion";
import { ViewSubsectionBulletPoints } from "../components/viewSubsectionBulletPoints";
import { ViewSubsectionDescription } from "../components/viewSubsectionDescription";
// import ViewSubsectionCalender from "../components/viewSubsectionCalender";
import PromotionSectionSkeleton from '../components/PromotionSectionSkeleton';

function Promotion() {
  const selectedLanguage = useSelector(
    (state) => state.language.selectedLanguage || "en"
  );
  const language = formatLanguage(selectedLanguage);
  const section = "Promotion";

  // Local states
  const [subsectionList, setSubsectionList] = useState([]);
  const [subsection, setSubsection] = useState("Steps of promotion process");
  const subsectionRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);
  const [isFaqExpanded, setIsFaqExpanded] = useState(false);
  const leftColumnRef = useRef(null);
  const [leftColumnHeight, setLeftColumnHeight] = useState(0);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const originPath = `${location.pathname}${location.search}${location.hash}`;

  // MUI Breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  // Fetch list of subsections
  useEffect(() => {
    setLoading(true);
    apiClient
      .post("/section_page/subsection_list", { language, section })
      .then((res) => {
        const list = res.data.data || [];
        const valid = list.map((item) => ({
          ...item,
          layout: item.layout || "default",
        }));
        setSubsectionList(valid);
        if (valid.length > 0) {
          setSubsection(valid[0].value);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [language, section]);

  // Fetch content helper for regular subsections
  const fetchContentData = async ({
    subsection,
    filter,
    language,
    section,
  }) => {
    try {
      const response = await apiClient.post(`/section_page/view_subsection/`, {
        subsection,
        section,
        language,
        filter: {
          ...filter,
          section_filter: "Promotion",
        },
      });
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  // Fetch content helper specifically for Government Resolutions
  const fetchGRContentData = async ({
    subsection,
    filter,
    language,
    section,
  }) => {
    try {
      const response = await apiClient.post(`/section_page/view_subsection/`, {
        subsection,
        section: "Probation", // Force section to Probation for Government Resolutions
        language,
        filter: {
          ...filter,
          section_filter: "Probation",
        },
      });
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  // Scroll to selected subsection
  useEffect(() => {
    if (subsectionRef.current) {
      subsectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [subsection]);

  // Sticky SubsectionList
  useEffect(() => {
    const layout = document.getElementById("layout");
    const secDiv = document.getElementById("sec-div");
    if (!layout || !secDiv) return;

    const handleScroll = () => {
      const secDivTop =
        secDiv.getBoundingClientRect().top - layout.getBoundingClientRect().top;
      setIsSticky(secDivTop <= 0);
    };

    layout.addEventListener("scroll", handleScroll);
    return () => layout.removeEventListener("scroll", handleScroll);
  }, []);

  // Identify current subsection (for Mobile)
  const subsectionItem = subsectionList.find(
    (item) => item.value === subsection
  );

  // Identify FAQ item
  const faqsItem = subsectionList.find((item) => item.value === "FAQs");

  const handleFaqExpand = (event, expanded) => {
    setIsFaqExpanded(expanded);
  };

  useEffect(() => {
    if (!leftColumnRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setLeftColumnHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(leftColumnRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxWidth: "100vw",
        overflow: "auto",
      }}
    >
      <Header />

      {/* Sticky SubsectionList (top) */}
      <div id="sec-div" className={isSticky && !isMobile ? "sticky" : ""}>
        <SubsectionList
          subsectionList={
            // Exclude 'FAQs' from the main list on Desktop/Tablet
            isMobile
              ? subsectionList
              : subsectionList.filter((item) => item.value !== "FAQs")
          }
          subsection={subsection}
          setSubsection={setSubsection}
        />
      </div>

      {/* Main Content - Responsive */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 2,
          p: 2,
          height: "auto",
          paddingBottom: "0px",
        }}
      >
        {/* ===================== MOBILE (Single Column) ===================== */}
        {isMobile && (
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* Render the chosen subsection */}
            {subsectionItem?.layout === "bulletpoints" && (
              <ViewSubsectionBulletPoints
                language={language}
                section={section}
                subsection={{ subsection: subsectionItem.value }}
                fetchContentData={fetchContentData}
              />
            )}
            {subsectionItem?.layout === "steps" && (
              <ViewSubsectionSteps
                language={language}
                section={section}
                subsection={{ subsection: subsectionItem.value }}
                fetchContentData={fetchContentData}
              />
            )}
            {subsectionItem?.layout === "accordion" && (
              <ViewSubsectionAccordion
                language={language}
                section={section}
                subsection={{ subsection: subsectionItem.value }}
                fetchContentData={fetchContentData}
              />
            )}
            {subsectionItem?.layout === "description" && (
              <ViewSubsectionDescription
                language={language}
                section={section}
                subsection={{ subsection: subsectionItem.value }}
                fetchContentData={fetchContentData}
              />
            )}

            {/* ChatBot (Mobile) */}
            <Box sx={{ mt: 2 }}>
              <ChatBotContainer
                fullScreen={false}
                onOpenFullPage={() =>
                  navigate("/chatbot", {
                    state: { fromPath: originPath },
                  })
                }
              />
            </Box>
          </Box>
        )}

        {/* ===================== TABLET / DESKTOP (Two Columns) ===================== */}
        {!isMobile && (
          <>
            {/* LEFT / MIDDLE COLUMN => FAQ + ChatBot together in one scrollable grid */}
            <Box
              ref={leftColumnRef}
              sx={{
                flex: { xs: 1, md: 1 / 3 },
                height: "100%",
                overflow: "hidden",
              }}
            >
              {/* FAQ (Top) */}
              {faqsItem && (
                <Box
                  sx={{
                    width: "100%",
                    marginBottom: 2,
                  }}
                  ref={subsection === faqsItem.value ? subsectionRef : null}
                >
                  {faqsItem.layout === "accordion" && (
                    <Accordion 
                      sx={{ width: "100%" }}
                      onChange={handleFaqExpand}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontSize: "1.25rem", fontWeight: "bold" }}>
                          {faqsItem.title || "FAQ"}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ width: "100%", p: 0 }}>
                        <ViewSubsectionAccordion
                          language={language}
                          section={section}
                          subsection={{ subsection: faqsItem.value }}
                          fetchContentData={fetchContentData}
                        />
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Box>
              )}

              {/* ChatBot (Bottom) */}
              <Box
                sx={{
                  minHeight: "500px",
                  height: "auto",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <ChatBotContainer
                  fullScreen={false}
                  onOpenFullPage={() =>
                    navigate("/chatbot", {
                      state: { fromPath: originPath },
                    })
                  }
                />
              </Box>
            </Box>
            {/* RIGHT COLUMN => all other subsections (excluding FAQ) */}
            <Box
              sx={{
                flex: { xs: 1, md: 2 / 3 },
                height: leftColumnHeight || "auto",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                scrollbarWidth: "thin",
                transition: "height 0.3s ease",
                "&::-webkit-scrollbar": {
                  width: "4px",
                  backgroundColor: "transparent",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#999",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#666",
                },
              }}
            >
              {loading ? (
                <PromotionSectionSkeleton />
              ) : (
                subsectionList
                  .filter((item) => item.value !== "FAQs")
                  .map((item) => (
                    <div
                      key={item.value}
                      id={item.value}
                      ref={subsection === item.value ? subsectionRef : null}
                    >
                      {item.layout === "bulletpoints" && (
                        <ViewSubsectionBulletPoints
                          language={language}
                          section={section}
                          subsection={{ subsection: item.value }}
                          fetchContentData={fetchContentData}
                        />
                      )}
                      {item.layout === "steps" && (
                        <ViewSubsectionSteps
                          language={language}
                          section={section}
                          subsection={{ subsection: item.value }}
                          fetchContentData={fetchContentData}
                        />
                      )}
                      {item.layout === "accordion" && (
                        <ViewSubsectionAccordion
                          language={language}
                          section={section}
                          subsection={{ subsection: item.value }}
                          fetchContentData={fetchContentData}
                        />
                      )}
                      {item.layout === "description" && (
                        <ViewSubsectionDescription
                          language={language}
                          section={section}
                          subsection={{ subsection: item.value }}
                          fetchContentData={fetchContentData}
                        />
                      )}
                    </div>
                  ))
              )}
            </Box>
          </>
        )}
      </Box>

      {/* Footer / Common UI at bottom (if needed) */}
      <CommonSubsectionUI
        language={language}
        section="Probation"
        subsection={subsection}
        subsectionRef={subsectionRef}
      />
    </div>
  );
}

export default Promotion;